import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { InlineNotificationComponent } from '@pure/hive';
import { IRichBlockCallout, RichBlockActionActivation } from '@pure1/data';
import { RichBlockActionButtonComponent } from '../rich-block-action-button/rich-block-action-button.component';
import { withSinglePrimary } from '../rich-block-emphasis';

/** Hive has four severities to the contract's three variants, so `next_steps` shares `info`'s. */
const CALLOUT_SEVERITY: Record<IRichBlockCallout['variant'], 'info' | 'warning'> = {
    insight: 'info',
    next_steps: 'info',
    warning: 'warning',
};

/**
 * One highlighted point — "Did you know?", "Your next steps", a warning. Rendered through
 * `hive-inline-notification`, which supplies the panel, wash, heading and icon.
 *
 * The body is plain text (FR-048): no markdown pipeline, no nested blocks. Actions project into
 * the message slot rather than the call-to-action slot, which is what puts them below the body.
 */
@Component({
    selector: 'copilot-rich-block-callout',
    templateUrl: './callout-block.component.html',
    styleUrl: './callout-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [InlineNotificationComponent, RichBlockActionButtonComponent],
})
export class CalloutBlockComponent {
    readonly block = input.required<IRichBlockCallout>();

    readonly activateAction = output<RichBlockActionActivation>();

    protected readonly severity = computed(() => CALLOUT_SEVERITY[this.block().variant] ?? 'info');

    /** Same rendering rule as a standalone `actions` block: the first primary wins (R2.4). */
    protected readonly actions = computed(() => withSinglePrimary(this.block().actions));

    /** `next_steps` is a direction, not a severity. Empty means hive falls back to its own glyph. */
    protected readonly icon = computed<IconDefinition | string>(() =>
        this.block().variant === 'next_steps' ? faArrowRight : '',
    );
}
